import type { UpgradeSourceElement } from '@shared/core/analytics'
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CalendarItem } from 'src/modules/calendar/components'
import { useCalendarById } from 'src/modules/calendar/hooks/use-calendar-by-id'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { useUpdateCalendar } from 'src/modules/calendar/hooks/use-update-calendar'
import {
  hasNumericCalendarLimit,
  isCalendarLimitReached,
  sortCalendars,
  usesLockedCalendarsCard,
} from 'src/modules/calendar/utils/helpers'
import { FormattedText, InfoModal, Loader, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { usePlanData } from 'src/modules/payment/hooks/use-plan-data'
import { buildUpgradePlansPath, resolveUpgradeSourcePage } from 'src/modules/payment/utils/upgrade-funnel-navigation'
import { paramsWithScheduledReminders } from 'src/modules/user/constants/profile-params'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { UpdateCalendarBodyDto } from 'src/types/calendar.dto'
import uniqid from 'uniqid'
import { LockedCalendarItem } from '../locked-calendar-item'
import { LockedCalendarsCard } from '../locked-calendars-card'
import type { CalendarListProps } from './calendar-list.interface'
import classes from './calendar-list.module.scss'

export const CalendarList: FC<CalendarListProps> = ({
  withUpgradeButton,
  title,
  listClassName,
  itemClassName,
  children,
}) => {
  // state
  const [updatedCalendar, setUpdatedCalendar] = useState<UpdateCalendarBodyDto>(null)
  const [unselectedCalendar, setUnselectedCalendar] = useState<UpdateCalendarBodyDto>(null)
  // hooks
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const calendars = useCalendars({}, { sync: true })
  const updateCalendar = useUpdateCalendar()
  const calendarById = useCalendarById(unselectedCalendar?.id, paramsWithScheduledReminders)
  const profile = useProfile()
  const { currentPlan, loading } = usePlanData({
    subscriptionOptions: { enabled: true },
  })
  // This cohort gets every calendar and has nothing to upgrade to, so no
  // calendar may be locked and no upgrade affordance may be offered.
  const hideBillingUi = Boolean(profile.data?.hide_billing_ui)
  const consolidateLockedCalendars = Boolean(withUpgradeButton) && usesLockedCalendarsCard(profile.data)
  // memo
  const isLoading = useMemo(
    () => calendars.isLoading || calendarById.isLoading || loading,
    [calendars.isLoading, calendarById.isLoading, loading],
  )

  const processedCalendars = useMemo(() => sortCalendars(calendars.data), [calendars.data])

  const selectedCalendars = processedCalendars.filter((item) => item.selected)
  const lockedCalendarIds = processedCalendars
    .filter(
      (calendar) =>
        !hideBillingUi && isCalendarLimitReached(currentPlan, calendar, selectedCalendars) && !calendar.selected,
    )
    .map((calendar) => calendar.id)
  const showActiveCount = consolidateLockedCalendars && !isLoading && hasNumericCalendarLimit(currentPlan)

  const buildUpgradePath = (sourceElement: UpgradeSourceElement) =>
    buildUpgradePlansPath({
      trigger: 'calendar_add_attempt',
      source_element: sourceElement,
      source_page: resolveUpgradeSourcePage(pathname, new URLSearchParams(search)),
    })

  const handleUpdateCalendar = useCallback(
    (calendar: UpdateCalendarBodyDto) => {
      setUpdatedCalendar(calendar)

      updateCalendar.mutate(calendar, {
        onSettled: () => setUpdatedCalendar(null),
      })
    },
    [updateCalendar],
  )

  const handleCheckboxChange = useCallback(
    (id: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const newCalendar: UpdateCalendarBodyDto = {
        id,
        selected: e.target.checked,
      }

      if (e.target.checked) {
        handleUpdateCalendar(newCalendar)
      } else {
        setUnselectedCalendar(newCalendar)
      }
    },
    [handleUpdateCalendar],
  )

  const onSuccess = useCallback(() => {
    handleUpdateCalendar(unselectedCalendar)
    setUnselectedCalendar(null)
  }, [handleUpdateCalendar, unselectedCalendar])

  const onClose = () => {
    setUnselectedCalendar(null)
  }

  useEffect(() => {
    if (calendarById.data && calendarById.data.include.count_scheduled_reminders === 0) {
      onSuccess()
    }
  }, [calendarById.data, onSuccess])

  return (
    <>
      {title && (
        <div className={classes.header}>
          {title}
          {showActiveCount && (
            <span className={classes.activeCount}>
              <FormattedText
                id="calendars.locked.activeCount"
                values={{ active: selectedCalendars.length, total: processedCalendars.length }}
              />
            </span>
          )}
        </div>
      )}
      {isLoading ? (
        <div className={classes.loadingWrapper}>
          <Loader isLoading={isLoading} />
        </div>
      ) : (
        <>
          <ul className={listClassName}>
            {processedCalendars.map((calendar) => {
              const limitReached = lockedCalendarIds.includes(calendar.id)
              const disabled = updateCalendar.isLoading || limitReached

              if (limitReached && consolidateLockedCalendars) {
                return (
                  <li key={uniqid()} className={classes.item}>
                    <LockedCalendarItem
                      calendar={calendar}
                      upgradePath={buildUpgradePath('sidebar_locked_calendar')}
                      className={itemClassName}
                    />
                  </li>
                )
              }

              return (
                <li key={uniqid()} className={classes.item}>
                  <CalendarItem
                    calendar={calendar}
                    selected={updatedCalendar?.id === calendar.id ? updatedCalendar.selected : calendar.selected}
                    disabled={disabled}
                    onCheckboxChange={handleCheckboxChange}
                    className={itemClassName}
                  />
                  {limitReached && withUpgradeButton && (
                    <Typography
                      variant="span"
                      className={classes.textButton}
                      onClick={() => navigate(`${routeNames.settings.path}?tab=plan`)}
                    >
                      <FormattedText id="calendars.dashboard.button" />
                    </Typography>
                  )}
                </li>
              )
            })}
          </ul>
          {consolidateLockedCalendars && lockedCalendarIds.length > 0 && (
            <LockedCalendarsCard
              variant="sidebar"
              lockedCount={lockedCalendarIds.length}
              upgradePath={buildUpgradePath('sidebar_unlock_calendars')}
              className={classes.lockedCard}
            />
          )}
          {unselectedCalendar && (
            <InfoModal
              title={<FormattedText id="calendars.warning.title" />}
              description={
                <FormattedText
                  id="calendars.warning.description"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                    count: calendarById.data.include.count_scheduled_reminders,
                  }}
                />
              }
              button={<FormattedText id="common.continue" />}
              onClick={onSuccess}
              onClose={onClose}
            />
          )}
          {children}
        </>
      )}
    </>
  )
}
